import React, { useEffect, useMemo, useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import Error from "Components/Error";
import PageMeta from "Components/PageMeta";
import { Button, ButtonWrapper } from "ds/Button";
import Input from "ds/Input";
import Loading from "ds/PageLoading";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { difference, isDifferent } from "Libs/difference";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { gitProjectSelector } from "Reducers/project/project";
import {
  buildResourcesProjectSelector,
  updateBuildResources,
  projectSettingsLoadingSelector,
  projectSettingsErrorSelector
} from "Reducers/projectSettings";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./BuildResources.styles";

import type { ProjectSetting } from "Reducers/projectSettings";

const BuildResources = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { projectId } = useDecodedParams<{
    projectId: string;
  }>();

  const projectCapabilities = useSelectorWithUrlParams(
    projectCapabilitySelector
  );
  const buildResources = useSelectorWithUrlParams(
    buildResourcesProjectSelector
  );

  const isLoading = useAppSelector(projectSettingsLoadingSelector);
  const error = useAppSelector(projectSettingsErrorSelector);

  const initialResources = useMemo(
    () => ({
      cpu: `${buildResources?.cpu ?? 0}`,
      memory: `${buildResources?.memory ?? 0}`
    }),
    [buildResources]
  );

  const [resources, setResources] = useState(initialResources);

  useEffect(() => {
    setResources(initialResources);
  }, [initialResources]);

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const onChangeResourcesHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setResources(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const onBlurResourcesHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, max, name } = e.target;

    const valueNumber = +value;
    const maxNumber = +max;
    const minNumber = +name;

    const acceptedNumber =
      valueNumber > maxNumber
        ? maxNumber
        : valueNumber < minNumber
          ? minNumber
          : valueNumber;

    setResources(prevState => ({
      ...prevState,
      [id]: `${acceptedNumber}`
    }));
  };

  const formHasChanged = isDifferent(initialResources, resources);
  const canEdit = project?.hasPermission("#edit");

  const revertFormChanges = () => {
    setResources(initialResources);
  };

  const submitForm = () => {
    const diff = difference(initialResources, resources);
    const formattedValue = Object.fromEntries(
      Object.entries(diff).map(([key, value]) => [key, Number(value)])
    );
    dispatch(
      updateBuildResources({
        projectId,
        buildResources: formattedValue as ProjectSetting["build_resources"]
      })
    );
  };

  return (
    <S.Section>
      <PageMeta title={`Resources | ${project?.title}`} />
      {isLoading ? (
        <Loading iconOnly />
      ) : (
        <>
          <S.Title tag="h2">
            {intl.formatMessage({
              id: "settings.resources.title",
              defaultMessage: "Resources"
            })}
          </S.Title>
          <S.BuildResources>
            <Typography.BodyText weight="bold">
              {intl.formatMessage({
                id: "settings.buildResources.title",
                defaultMessage: "Build resources"
              })}
            </Typography.BodyText>
            <Typography.BodyText>
              {intl.formatMessage({
                id: "settings.buildResources.description",
                defaultMessage:
                  "Configure your backup schedules for each environment type for this project."
              })}
            </Typography.BodyText>
            <S.Form>
              <Input
                id="cpu"
                max={projectCapabilities?.build_resources?.max_cpu}
                name="0.1"
                analyticId="settings.buildResources.cpu.input.label"
                step={0.1}
                label={intl.formatMessage({
                  id: "settings.buildResources.cpu.input.label"
                })}
                required
                value={resources.cpu}
                onChange={onChangeResourcesHandler}
                onBlur={onBlurResourcesHandler}
                width="100%"
                type="number"
                disabled={!canEdit}
              />
              <S.RamInputWrapper>
                <Input
                  analyticId="settings.buildResources.ram.input.label"
                  id="memory"
                  max={projectCapabilities?.build_resources?.max_memory}
                  name="64"
                  label={intl.formatMessage({
                    id: "settings.buildResources.ram.input.label"
                  })}
                  required
                  value={resources.memory}
                  onChange={onChangeResourcesHandler}
                  onBlur={onBlurResourcesHandler}
                  width="100%"
                  type="number"
                  disabled={!canEdit}
                />
                <S.Blank
                  gapSpace={`${resources.memory}`.length * 9}
                  disabled={!canEdit}
                >
                  {intl.formatMessage({
                    id: "settings.buildResources.ram.input.MB",
                    defaultMessage: "MB"
                  })}
                </S.Blank>
              </S.RamInputWrapper>
            </S.Form>
            <Typography.Label>
              {intl.formatMessage<React.ReactNode>(
                { id: "settings.buildResources.note" },
                {
                  link: chunks => (
                    <S.Link
                      analyticId="links.pricing"
                      variant="link"
                      href={intl.formatMessage({ id: "links.pricing" })}
                    >
                      {chunks}
                    </S.Link>
                  )
                }
              )}
            </Typography.Label>
            {formHasChanged && (
              <ButtonWrapper>
                <Button onClick={submitForm} analyticId="save">
                  {intl.formatMessage({
                    id: "save"
                  })}
                </Button>
                <Button
                  variant="secondary"
                  analyticId="cancel"
                  onClick={revertFormChanges}
                >
                  {intl.formatMessage({
                    id: "cancel"
                  })}
                </Button>
              </ButtonWrapper>
            )}
            <Error>{error}</Error>
          </S.BuildResources>
        </>
      )}
    </S.Section>
  );
};

export default BuildResources;
